import { createFileRoute } from '@tanstack/react-router';
import { createServerFn } from '@tanstack/react-start';
import AccommodationListPage from '../../pages/AccommodationListPage';
import { getExtraInformation } from '../../server/getExtraInformation';
import { getCountries } from '../../server/getCountries';
import { getDestinationsByCountry } from '../../server/getDestinationsByCountry';
import { searchProperties } from '../../server/searchProperties';
import { destinationMiddleware } from '../../server/middleware/destinationMiddleware';
import { sanitizeHtml } from '../../server/utlis/sanitizeHtml';
import { stripParagraphBr } from '../../server/utlis/stripParagraphBr';
import { replaceDestinationName } from '../../server/utlis/replaceDestinationName';
import { generateItemListSchema, generateBreadcrumbSchema } from '../../utils/jsonLd';

const loadDestination = createServerFn()
    .middleware([destinationMiddleware])
    .handler(async ({ context }) => {
        return context.destination;
    });

export const Route = createFileRoute('/accommodation/')({
    beforeLoad: async () => {
        try {
            const destination = await loadDestination();
            return { destination };
        } catch (error) {
            console.error('Failed to load destination:', error);
            throw error;
        }
    },
    loader: async ({ context }) => {
        const { destination } = context;
        const extractIdVal = (val) => {
            if (!val) return null;
            if (typeof val === 'string') return val;
            if (typeof val === 'object') return val._id || val.id || null;
            return String(val);
        };
        const countryId = extractIdVal(destination?.country);
        const destinationId = extractIdVal(destination);

        const [extraInfo, countriesList, initialDestinationsList, initialPropertiesData] = await Promise.all([
            getExtraInformation({ data: { displayPage: 'Accommodation' } }).catch(() => null),
            getCountries().catch(() => []),
            countryId ? getDestinationsByCountry({ data: { countryId } }).catch(() => []) : Promise.resolve([]),
            countryId && destinationId
                ? searchProperties({
                    data: {
                        country: countryId,
                        destination: destinationId,
                    },
                }).catch(() => ({}))
                : Promise.resolve({}),
        ]);
        if (extraInfo) {
            extraInfo.steps = await Promise.all(
                (extraInfo.steps || []).map(async (step) => ({
                    ...step,
                    description: await sanitizeHtml({
                        data: {
                            html: await replaceDestinationName({
                                data: {
                                    text: await stripParagraphBr({
                                        data: { html: step.description },
                                    }),
                                    destinationName: destination?.name,
                                },
                            }),
                        },
                    }),
                }))
            );
            extraInfo.description = await stripParagraphBr({ data: { html: extraInfo.description } });
            extraInfo.description = await replaceDestinationName({ data: { text: extraInfo.description, destinationName: destination?.name } });
            extraInfo.title = await replaceDestinationName({ data: { text: extraInfo.title, destinationName: destination?.name } });
            extraInfo.description = await sanitizeHtml({ data: { html: extraInfo.description } });
        }
        return {
            destination,
            extraInfo,
            countriesList,
            initialDestinationsList,
            initialPropertiesData,
        };
    },
    // Server-rendered — see routes/index.jsx for why this replaces Helmet.
    head: ({ loaderData }) => {
        const destination = loaderData?.destination;
        const properties = loaderData?.initialPropertiesData?.properties || [];
        const title =
            destination?.metaData?.accommodationPage?.title ||
            `${destination?.name || 'Maasai Mara'} | Book Accommodation Directly`;
        const description =
            destination?.metaData?.accommodationPage?.description ||
            `Planning to visit ${destination?.name || 'Maasai Mara'}? We help you find your dream accommodation in ${destination?.name || 'Maasai Mara'} and connect you directly with the properties or hosts. You are in charge!`;
        const image = destination?.images?.accommodation?.[0]?.url;
        const siteUrl = (destination?.url || 'https://www.maasaimara.com').replace(/\/$/, '');
        const canonicalUrl = `${siteUrl}/accommodation`;

        const itemListSchema = generateItemListSchema(properties, siteUrl);
        const breadcrumbSchema = generateBreadcrumbSchema(
            [
                { title: 'Home', url: '/' },
                { title: 'Accommodations', url: '/accommodation' },
            ],
            siteUrl
        );

        return {
            meta: [
                { title },
                { name: 'description', content: description },
                { property: 'og:title', content: title },
                { property: 'og:description', content: description },
                { property: 'og:type', content: 'website' },
                { property: 'og:url', content: canonicalUrl },
                ...(image
                    ? [
                        { property: 'og:image', content: image },
                        { property: 'og:image:type', content: 'image/jpeg' },
                    ]
                    : []),
            ],
            links: [
                { rel: 'canonical', href: canonicalUrl },
            ],
            scripts: [
                ...(itemListSchema
                    ? [{ type: 'application/ld+json', children: JSON.stringify(itemListSchema) }]
                    : []),
                ...(breadcrumbSchema
                    ? [{ type: 'application/ld+json', children: JSON.stringify(breadcrumbSchema) }]
                    : []),
            ],
        };
    },
    component: AccommodationListPage,
    // ── Caching config 
    staleTime: 5 * 60 * 1000,   // reuse cached data for 5 mins on re-navigation
    gcTime: 10 * 60 * 1000,  // keep in memory for 10 mins after route unmounts
});